import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { dispatch } from "../index";
import {
  mergeShiftMutationResponse,
  normalizePeriod,
  normalizePeriodsResponse,
  removePeriod,
  removeShiftFromPeriod,
  upsertPeriod,
} from "./siteShifts.helpers";

export const initialState = {
  periods: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
  lastFetchedSiteId: null,
};

const slice = createSlice({
  name: "siteShifts",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setSaving(state, action) {
      state.saving = action.payload;
    },
    setDeleting(state, action) {
      state.deleting = action.payload;
    },
    setPeriods(state, action) {
      state.periods = action.payload;
      state.error = null;
    },
    setLastFetchedSiteId(state, action) {
      state.lastFetchedSiteId = action.payload;
    },
    upsertPeriodSuccess(state, action) {
      state.periods = upsertPeriod(state.periods, action.payload);
      state.error = null;
    },
    removePeriodSuccess(state, action) {
      state.periods = removePeriod(state.periods, action.payload);
      state.error = null;
    },
    mergeShiftMutationSuccess(state, action) {
      state.periods = mergeShiftMutationResponse(
        state.periods,
        action.payload.periodId,
        action.payload.response,
      );
      state.error = null;
    },
    removeShiftSuccess(state, action) {
      state.periods = removeShiftFromPeriod(
        state.periods,
        action.payload.periodId,
        action.payload.shiftId,
      );
      state.error = null;
    },
    clearSiteShifts(state) {
      state.periods = [];
      state.lastFetchedSiteId = null;
      state.error = null;
    },
  },
});

export default slice.reducer;

export function getPeriods(siteId) {
  return async () => {
    dispatch(slice.actions.setLoading(true));

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/config/config/period`,
        {
          params: { site: siteId },
        },
      );

      const periods = normalizePeriodsResponse(response);
      dispatch(slice.actions.setPeriods(periods));
      dispatch(slice.actions.setLastFetchedSiteId(siteId));

      return periods;
    } catch (error) {
      // Axios may treat 204 as an error in some edge middleware paths.
      if (error?.response?.status === 204) {
        dispatch(slice.actions.setPeriods([]));
        dispatch(slice.actions.setLastFetchedSiteId(siteId));
        return [];
      }

      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

export function createPeriod(payload) {
  return async () => {
    dispatch(slice.actions.setSaving(true));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/config/config/period`,
        payload,
      );

      const period = normalizePeriod(response?.data || payload);
      dispatch(slice.actions.upsertPeriodSuccess(period));

      return period;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setSaving(false));
    }
  };
}

export function updatePeriod(periodId, payload) {
  return async () => {
    dispatch(slice.actions.setSaving(true));

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API}/config/config/period/${periodId}`,
        payload,
      );

      const period = normalizePeriod({ ...payload, ...response?.data, id: periodId });
      dispatch(slice.actions.upsertPeriodSuccess(period));

      return period;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setSaving(false));
    }
  };
}

export function deletePeriod(periodId) {
  return async () => {
    dispatch(slice.actions.setDeleting(true));

    try {
      await axios.delete(`${process.env.REACT_APP_API}/config/config/period/${periodId}`);
      dispatch(slice.actions.removePeriodSuccess(periodId));
      return true;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setDeleting(false));
    }
  };
}

export function createShift(periodId, payload) {
  return async () => {
    dispatch(slice.actions.setSaving(true));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/config/config/period/${periodId}/shift`,
        payload,
      );

      dispatch(
        slice.actions.mergeShiftMutationSuccess({
          periodId,
          response: response?.data,
        }),
      );

      return response?.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setSaving(false));
    }
  };
}

export function updateShift(periodId, shiftId, payload) {
  return async () => {
    dispatch(slice.actions.setSaving(true));

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API}/config/config/period/${periodId}/shift/${shiftId}`,
        payload,
      );

      dispatch(
        slice.actions.mergeShiftMutationSuccess({
          periodId,
          response: response?.data,
        }),
      );

      return response?.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setSaving(false));
    }
  };
}

export function deleteShift(periodId, shiftId) {
  return async () => {
    dispatch(slice.actions.setDeleting(true));

    try {
      await axios.delete(
        `${process.env.REACT_APP_API}/config/config/period/${periodId}/shift/${shiftId}`,
      );

      dispatch(slice.actions.removeShiftSuccess({ periodId, shiftId }));
      return true;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    } finally {
      dispatch(slice.actions.setDeleting(false));
    }
  };
}

export function clearSiteShifts() {
  return () => {
    dispatch(slice.actions.clearSiteShifts());
  };
}
